import api from '@/lib/api/axios'
import type { AgentEvent, AuditEntry } from './bridge'

/** A restore run as Orchestrate records it (backend/src/lib/restore-runs.js). */
export type RunStatus = 'queued' | 'running' | 'waiting_for_person' | 'paused' | 'succeeded' | 'refused' | 'failed' | 'cancelled' | 'usage_error' | 'lost'
export interface RestoreRun {
    runId: string
    command: string
    mode: string | null
    targetId: string | null
    objectId: string | null
    startedByName: string | null
    machine: string | null
    agentVersion: string | null
    status: RunStatus
    currentStep: string | null
    percent: number | null
    etaSeconds: number | null
    needsPerson: { code: string | null; what: string | null; who: string | null; why: string | null; where: string | null } | null
    startedAt: string | null
    finishedAt: string | null
    exitCode: number | null
    outcome: string | null
    code: string | null
    rehearsal: boolean | null
    targetChanged: 'no' | 'yes' | 'unknown' | null
    failure: { type: string; code: string | null; meaning?: string | null; remedy?: string | null; message?: string | null; target_state?: string | null; operator_must?: string | null } | null
    verified: Array<{ what: string; how: string }>
    warnings: number
    lastSeq: number
    lastEventAt: string | null
    createdAt: string | null
    active: boolean
    /** Set when the result was pasted in from a result token: the engineer's report, not the agent's own events. */
    reportedByName?: string | null
    reportedAt?: string | null
    events?: AgentEvent[]
}

/** The macOS package (daemon + restore agent) on offer here; a test build only in Vault Sentinel's own environment. */
export type SetupPackage =
    | { published: false; reason: 'not_set_up' | 'not_published' }
    | { published: true; version: string; file: string; sha256: string; bytes: number | null; test: boolean; notes: string | null; daemonVersion: string | null; agentVersion: string | null }
/** One restore agent build, as the restore agent session's manifest describes it. */
/** Which restore agent release the package carries - never its files: the agent is not downloaded on its own. */
export type SetupAgents =
    | { published: false; reason: 'not_set_up' | 'not_published' }
    | { published: true; release: string; commit: string | null; signed: boolean; note: string | null; test: boolean }
export interface SetupReleases { package: SetupPackage; agents: SetupAgents }

type Envelope<T> = { success: boolean; data: T }
export const restoreRunsApi = {
    /** This person's recent runs; every command unless one is named. */
    mine: (command?: string) => api.get<Envelope<RestoreRun[]>>('/api/restore-runs', { params: { ...(command ? { command } : {}), mine: 1, limit: 15 } }),
    get: (runId: string) => api.get<Envelope<RestoreRun>>(`/api/restore-runs/${encodeURIComponent(runId)}`),
    /** targetId / objectId are the ids `restorePlan` returned: the database refuses one already in use. */
    create: (body: { runId: string; command: string; request: unknown; targetId?: string | null; objectId?: string | null }) => api.post<Envelope<RestoreRun>>('/api/restore-runs', body),
    events: (runId: string, events: AgentEvent[]) => api.post<Envelope<{ run: RestoreRun; accepted: number; ignored: number }>>(`/api/restore-runs/${encodeURIComponent(runId)}/events`, { events }),
    notStarted: (runId: string, reason: string) => api.post<Envelope<RestoreRun>>(`/api/restore-runs/${encodeURIComponent(runId)}/not-started`, { reason }),
    /** A short sign-in pass for the signed-in engineer, signed by the back end. Sent to the plug-in unchanged. */
    sessionPass: () => api.post<Envelope<{ token: string; passId: string; notBefore: string; notAfter: string }>>('/api/restore-runs/session-pass'),
    /** An approval for the signed-in engineer: targets and kinds of action, at most 12 hours. Needs a fresh device proof. */
    approval: (body: { targets: string[]; operations: Array<'read' | 'restore' | 'destroy'>; hours?: number }) =>
        api.post<Envelope<{ approvalId: string; capability: string; notAfter: string }>>('/api/restore-runs/approvals', body),
    /** One signed restore job. Only the NAMES of its secrets: the values never come to the back end. */
    signJob: (body: { runId: string; command: string; operationClass: 'read' | 'restore' | 'destroy'; targetRef: string; options: Record<string, string | number | boolean>; secrets: string[]; rateLimitBytesPerSecond?: number; remote?: Record<string, unknown> }) =>
        api.post<Envelope<{ jobId: string; runId: string; job: string }>>('/api/restore-runs/jobs', body),
    /** The daemon's audit records, as handed to the page. One already held is ignored. */
    audit: (entries: AuditEntry[]) => api.post<Envelope<{ received: number; stored: number; alreadyHeld: number }>>('/api/restore-runs/audit', { entries }),
    auditGap: (missing: number[], lastSeq: number) => api.post<Envelope<unknown>>('/api/restore-runs/audit/gap', { missing, lastSeq }),
    /** The result token (VSRR1...) the agent printed at the end of a run made by hand. It holds no secret. */
    resultToken: (runId: string, token: string) => api.post<Envelope<RestoreRun>>(`/api/restore-runs/${encodeURIComponent(runId)}/result-token`, { token }),
    /** What the restore agent dialog can offer: the package (version, SHA-256) and the agent release it carries. No links. */
    setupReleases: () => api.get<Envelope<SetupReleases>>('/api/restore-runs/setup/releases'),
    /** A ten-minute link to the package, recorded as a download. */
    setupDownload: (body: { kind: 'package' }) =>
        api.post<Envelope<{ url: string; file: string; version: string | null; sha256: string; linkSeconds: number }>>('/api/restore-runs/setup/download', body),
    /** What this laptop was seen running: a new record when the versions change. */
    setupSeen: (body: { browser: string; os: string; machine: string | null; pluginVersion: string; daemonVersion: string | null; agentVersion: string | null }) =>
        api.post<Envelope<{ changed: boolean }>>('/api/restore-runs/setup/seen', body),
    /** Something on the laptop answered as the daemon and is not it: recorded as an incident. */
    setupIncident: (body: { code: string; message?: string | null; browser?: string | null }) => api.post<Envelope<unknown>>('/api/restore-runs/setup/incident', body),
    lost: (runId: string) => api.post<Envelope<RestoreRun>>(`/api/restore-runs/${encodeURIComponent(runId)}/lost`),
}
